import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { CUSTOMER_PORTAL_BRAND } from '../brand';

/** Matches the brand suffix route titles carry today, with either separator ("—" or "--"). */
const BRAND_SUFFIX_RE = new RegExp(`\\s+(?:—|--)\\s+${CUSTOMER_PORTAL_BRAND}$`);

/**
 * Localised document titles (walkabout 2026-09-28: German sessions kept English tab titles).
 *
 * Route `title:` strings stay the English source of truth. For the active language the strategy
 * looks up `pageTitles.<full route path>` in the ROOT catalog (e.g. `pageTitles.payments/gpi-tracker`)
 * and uses it when present; otherwise the English route title is kept. Either way the brand suffix
 * is normalised to one form (" — CashBro"), so "--" and "—" titles no longer mix.
 *
 * The title is re-applied when the language changes, unless a component has since set its own
 * title (a few pages compute a translated title themselves -- we never overwrite those).
 */
@Injectable({ providedIn: 'root' })
export class TranslatedTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly transloco = inject(TranslocoService);
  private last: { snapshot: RouterStateSnapshot; applied: string } | null = null;

  constructor() {
    super();
    this.transloco.events$.subscribe((event) => {
      if (event.type !== 'translationLoadSuccess' && event.type !== 'langChanged') return;
      const last = this.last;
      if (last && this.title.getTitle() === last.applied) this.updateTitle(last.snapshot);
    });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const english = this.buildTitle(snapshot);
    if (english === undefined) {
      this.last = null;
      return;
    }
    const applied = this.localise(english, routePath(snapshot.root));
    this.title.setTitle(applied);
    this.last = { snapshot, applied };
  }

  private localise(english: string, path: string): string {
    const page = english.replace(BRAND_SUFFIX_RE, '');
    const name = (path && this.lookup(`pageTitles.${path}`)) || page;
    return BRAND_SUFFIX_RE.test(english) ? `${name} — ${CUSTOMER_PORTAL_BRAND}` : name;
  }

  /**
   * Reads the key straight from the loaded catalogs (active language, then the fallback) instead
   * of `translate()`: most routes have no page-title entry and the root catalog may not have
   * loaded yet on the first navigation, and `translate()` would log a missing-key warning for each
   * of those in debug builds. The catalog load re-applies the title (see the constructor).
   */
  private lookup(key: string): string | null {
    const fallback = this.transloco.config.fallbackLang;
    const langs = [this.transloco.getActiveLang(), ...(Array.isArray(fallback) ? fallback : [fallback])];
    for (const lang of langs) {
      if (!lang) continue;
      const value = (this.transloco.getTranslation(lang) as Record<string, unknown> | undefined)?.[key];
      if (typeof value === 'string' && value.trim()) return value;
    }
    return null;
  }
}

/** Full configured path of the deepest activated route, e.g. `payments/gpi-tracker`. */
export function routePath(root: ActivatedRouteSnapshot): string {
  const parts: string[] = [];
  let node: ActivatedRouteSnapshot | null = root;
  while (node) {
    const path = node.routeConfig?.path;
    if (path) parts.push(path);
    node = node.firstChild;
  }
  return parts.join('/');
}
